import {Refresh, Chat, ContentCopy, KeyboardArrowDown, KeyboardArrowUp, ErrorOutline, Home} from '@mui/icons-material';
import {Box, Button, Typography, Paper, Collapse, IconButton, useTheme} from '@mui/material';
import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';

// On suppose que cette fonction ouvre Crisp ou votre outil de chat
const openCrisp = () => {
  if (window.$crisp) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    window.$crisp.push(['do', 'chat:open']);
  } else {
    window.open('mailto:contact@perfectpost.fr');
  }
};

interface ErrorFallbackProps {
  error: Error;
  resetError: () => void;
}

export const ErrorFallback = ({error, resetError}: ErrorFallbackProps) => {
  const {t} = useTranslation();
  const theme = useTheme();
  const [showDetails, setShowDetails] = useState(false);

  const handleCopyError = () => {
    navigator.clipboard.writeText(`${error.name}: ${error.message}\n${error.stack}`);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        bgcolor: 'background.default',
        p: 3,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          maxWidth: 550,
          width: '100%',
          p: 4,
          borderRadius: 3,
          border: `1px solid ${theme.palette.divider}`,
          textAlign: 'center',
          bgcolor: 'background.paper',
        }}
      >
        {/* Icone principale */}
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: '50%',
            bgcolor: 'error.lighter', // Ou 'rgba(211, 47, 47, 0.08)' si pas dans le thème
            color: 'error.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mx: 'auto',
            mb: 3,
          }}
        >
          <ErrorOutline sx={{fontSize: 32}} />
        </Box>

        <Typography variant="h5" gutterBottom fontWeight="700">
          {t('error.fallback.title', 'Oups, une erreur est survenue')}
        </Typography>

        <Typography variant="body1" color="text.secondary" sx={{mb: 4}}>
          {t(
            'error.fallback.description',
            'Ne vous inquiétez pas, cela arrive parfois. Essayez de rafraîchir la page, cela résout souvent le problème.',
          )}
        </Typography>

        {/* Actions Principales */}
        <Box sx={{display: 'flex', gap: 2, justifyContent: 'center', mb: 4}}>
          <Button variant="contained" size="large" startIcon={<Refresh />} onClick={resetError} sx={{px: 4}}>
            {t('action.reload', 'Rafraîchir la page')}
          </Button>
          <Button variant="outlined" size="large" startIcon={<Home />} onClick={() => (window.location.href = '/')}>
            {t('action.home', 'Accueil')}
          </Button>
        </Box>

        {/* Bloc Support (Style repris de StepCheckExtension) */}
        <Box
          sx={{
            p: 2.5,
            bgcolor: 'rgba(211, 47, 47, 0.04)', // Très léger rouge
            borderRadius: 2,
            border: '1px solid rgba(211, 47, 47, 0.12)',
            textAlign: 'left',
          }}
        >
          <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2}}>
            <Box>
              <Typography variant="subtitle2" fontWeight={600} color="text.primary">
                {t('error.fallback.support.title', 'Le problème persiste ?')}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                {t('error.fallback.support.desc', 'Notre équipe technique est là pour vous aider.')}
              </Typography>
            </Box>
            <Button
              variant="outlined"
              color="error"
              size="small"
              startIcon={<Chat />}
              onClick={openCrisp}
              sx={{bgcolor: 'background.paper', whiteSpace: 'nowrap'}}
            >
              {t('action.contact_support', 'Contacter le support')}
            </Button>
          </Box>
        </Box>

        {/* Détails techniques (Collapsible) */}
        <Box sx={{mt: 3}}>
          <Button
            size="small"
            color="inherit"
            endIcon={showDetails ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
            onClick={() => setShowDetails(!showDetails)}
            sx={{textTransform: 'none', color: 'text.secondary', fontSize: '0.75rem'}}
          >
            {t('error.fallback.details', 'Détails techniques (pour les développeurs)')}
          </Button>

          <Collapse in={showDetails}>
            <Box
              sx={{
                mt: 2,
                p: 2,
                bgcolor: 'action.hover',
                borderRadius: 2,
                border: `1px solid ${theme.palette.divider}`,
                textAlign: 'left',
                position: 'relative',
              }}
            >
              <IconButton
                size="small"
                onClick={handleCopyError}
                sx={{position: 'absolute', top: 8, right: 8}}
                title={t('error.fallback.copy')}
                aria-label={t('error.fallback.copy')}
              >
                <ContentCopy fontSize="small" />
              </IconButton>
              <Typography
                variant="caption"
                component="pre"
                // Retour à la ligne, et une marge à droite pour ne pas passer sous le bouton de copie.
                sx={{
                  m: 0,
                  pr: 5,
                  whiteSpace: 'pre-wrap',
                  overflowWrap: 'anywhere',
                  fontFamily: 'monospace',
                  color: 'error.main',
                }}
              >
                {error.toString()}
              </Typography>
            </Box>
          </Collapse>
        </Box>
      </Paper>
    </Box>
  );
};
